import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { getLocale, translate } from './i18n';

/*
 * tailwind-merge knows Tailwind's own sizes, not ours: without this it reads `text-caption` as a
 * colour, and `cn('text-caption', 'text-muted-foreground')` keeps only the second — the caption
 * silently renders at body size (context STORY-0264). Every `--text-*` step in `styles.css` is listed.
 */
const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: ['title', 'heading', 'caption', 'overline'] }] } },
});

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/**
 * CLDR unit names for the sizes we show. They are the *decimal* prefixes while the arithmetic below
 * is binary (1024), so "1 KB" here means 1024 bytes — which is what this product has always shown and
 * what a mail administrator reading a Dovecot quota expects. `Intl` has no kibibyte, so the choice is
 * between this and hand-written unit strings in every catalogue; localising the number and the space
 * rules is worth more than the prefix pedantry.
 */
const UNITS = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'] as const;

/** A size, formatted for the active locale — `1.5 GB` in English, `1,5 GB` in Spanish. */
export function formatBytes(bytes: number, locale = getLocale()): string {
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < UNITS.length - 1) {
    n /= 1024;
    i++;
  }
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: UNITS[i] ?? 'terabyte',
    // `short` everywhere except raw bytes, where CLDR's short form does not agree with the number:
    // English renders `512 byte`. `long` is the correctly pluralised `512 bytes`, and the prefixed
    // units have no such problem — their short forms (`kB`, `MB`) are the ones people expect.
    unitDisplay: i === 0 ? 'long' : 'short',
    maximumFractionDigits: 1,
  }).format(n);
}

/**
 * A quota. **0 bytes means "unlimited" throughout the API**, so this is the function to reach for
 * wherever the number is a limit rather than a measurement; `formatBytes` is the one for a size that
 * is genuinely zero.
 */
export const formatQuota = (bytes: number): string =>
  bytes === 0 ? translate('common.unlimited') : formatBytes(bytes);

/**
 * Parses "5 GB", "512MB", "0" (unlimited) into bytes; returns null when unparseable.
 *
 * The unit letters stay ASCII in every language — `Intl` renders `GB` for Spanish too, and a mail
 * administrator types what the field shows. The **decimal separator** does not: Spanish writes
 * `1,5 GB`, so a comma is accepted as a decimal point when the input has no dot of its own.
 */
export function parseBytes(input: string): number | null {
  const normalised = input.includes('.') ? input : input.replace(',', '.');
  // `bytes` spelled out as well as `B`, because that is what `formatBytes` renders below a kilobyte.
  const m = /^\s*(\d+(?:\.\d+)?)\s*([kmgt]?)(?:b(?:ytes?)?)?\s*$/i.exec(normalised);
  if (!m) return null;
  const n = Number(m[1]);
  const exp = { '': 0, k: 1, m: 2, g: 3, t: 4 }[(m[2] ?? '').toLowerCase()];
  if (exp === undefined) return null;
  return Math.round(n * 1024 ** exp);
}

/** A date and time, in the active locale's own order and calendar. */
export const formatDate = (iso: string, locale = getLocale()) =>
  new Date(iso).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });

/** A time of day, as the active locale writes it (`10:44` or `10:44 AM`): the composer's _Saved_ line. */
export const formatTime = (date: Date, locale = getLocale()) =>
  date.toLocaleTimeString(locale, { timeStyle: 'short' });

/** A plain number — thousands separators are `.` in Spanish and `,` in English. */
export const formatNumber = (n: number, locale = getLocale()) => new Intl.NumberFormat(locale).format(n);

/**
 * A calendar day (`2026-10-18`, a UTC date as the API counts days) for the active locale: `Oct 18` in
 * English, `18 oct` in Spanish (context TASK-0274). Read in UTC, so the day is the one the API meant
 * whatever the reader's time zone.
 */
export const formatDay = (isoDate: string, locale = getLocale()) =>
  new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${isoDate}T00:00:00Z`),
  );

/** A percentage, from a whole number of percent (`92` → `92%`). */
export const formatPercent = (percent: number, locale = getLocale()) =>
  new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(percent / 100);

/**
 * Whether **this row's** action is the one in flight (context STORY-0264). A list renders one button
 * per row against one mutation, and `loading={m.isPending}` spins every row's button at once — the
 * audit found that on four screens. The mutation's `variables` say which row it was called for.
 *
 * `match` is the row's key, compared with `===`, or a test for a mutation whose variables are an
 * object (`(v) => v.id === row.id`).
 */
export function pendingFor<V>(
  m: { isPending: boolean; variables?: V | undefined },
  match: V | ((variables: V) => boolean),
): boolean {
  if (!m.isPending || m.variables === undefined) return false;
  return typeof match === 'function' ? (match as (v: V) => boolean)(m.variables) : m.variables === match;
}
